import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { AppointmentController, FeedbackController } from 'api';
import { findManyClientsByIds } from 'api/ContactController';
import { OfferDetailsType } from 'api/OfferController';
import errorReporter from '@app/errorReporter';
import { Feature } from '@bb/automaton';
import MobileMapsDrawer from '@app/external/MobileMapsDrawer';
import CoopCalendarSyncButton from '@app/business/ManageAppointmentPage/CoopCalendarSyncButton';

import { Button, message, theme } from 'components';
import {
  buildGoogleMapsAddress,
  detectIOSDevice,
  correctShowingTypeLabel,
} from 'utils';

import DetailRow from './DetailRow';
import Section from './Section';
import {
  Covid19StateType,
  ShowingDetailsAppointmentType,
  ShowingDetailsListingType,
  ShowingDetailsPageUserType,
} from '../types';
import InviteBuyer, { SearchResult } from '../../../components/InviteBuyer';
import SendToClients from './SendToClients';
import DashboardTour from '../../MyBusinessPage/DashboardTour';

interface DetailsSectionProps {
  hasFeature: (feature: Feature) => boolean;
  userType: ShowingDetailsPageUserType;
  isListingAgent?: boolean;
  appointment: ShowingDetailsAppointmentType;
  listing: ShowingDetailsListingType;
  offerDetails?: OfferDetailsType;
  tourDetails?: any;
  covid19State: Covid19StateType;
  feedbackSubmitted: boolean;
  showFeedback: boolean;
  scrollToFeedback: () => void;
  listingAppointmentConfig: any;
  dashboardId?: string;
  queryParams?: Record<string, string>;
  fromTourPage?: boolean;
}

const DetailsSection: React.FC<DetailsSectionProps> = ({
  hasFeature,
  isListingAgent,
  userType,
  appointment,
  listing,
  offerDetails,
  tourDetails,
  covid19State,
  feedbackSubmitted,
  showFeedback,
  scrollToFeedback,
  listingAppointmentConfig,
  dashboardId,
  queryParams,
  fromTourPage,
}) => {
  const isIOSDevice = detectIOSDevice();
  const [iosDrawerVisible, setIosDrawerVisible] = useState(false);
  const [destinationAddress, setDestinationAddress] = useState('');

  const clickDirections: React.MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    const destination = buildGoogleMapsAddress(listing as any);
    if (isIOSDevice) {
      setDestinationAddress(destination);
      setIosDrawerVisible(true);
    } else {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
        '_blank',
      );
    }
  };

  const [buyersUpdated, setBuyersUpdated] = useState(false);
  const [savingBuyers, setSavingBuyers] = useState(false);
  const [inviteBuyers, setInviteBuyers] = useState<SearchResult[]>([]);

  const getBuyersLabel = async (buyers?: string[]) => {
    try {
      const buyerContacts = buyers?.length
        ? await findManyClientsByIds(buyers)
        : [];

      setInviteBuyers(
        buyerContacts
          .map(buyer => ({
            label: `${buyer.firstName} ${buyer.lastName}`,
            value: buyer.id!,
          }))
          .sort((buyer1: { label: string }, buyer2: { label: string }) =>
            buyer1.label.localeCompare(buyer2.label),
          ),
      );
    } catch (err) {
      errorReporter.error(`Error loading buyers: ${err}`);
    }
  };

  const updateBuyers = async () => {
    setSavingBuyers(true);
    try {
      await AppointmentController.saveBuyers(
        appointment.id,
        inviteBuyers.map(buyer => buyer.value),
      );
    } catch (err) {
      errorReporter.error(`Error saving buyers: ${err}`);
    }
    setBuyersUpdated(false);
    setSavingBuyers(false);
  };

  useEffect(() => {
    if (userType === 'coop') getBuyersLabel(appointment.buyers);
    else getBuyersLabel([]);
  }, [userType, appointment.buyers]);

  const getCovid19Text = () => {
    const { status, action } = covid19State;
    switch (status) {
      case 'overridden':
        return 'COVID-19 waiver overridden';
      case 'uploaded':
        return 'COVID-19 waiver uploaded';
      case 'uploadedLA':
      case 'signed':
        return action;
      case 'signatureRequired':
        return (
          <>
            COVID-19 waiver required
            <br />
            {action}
          </>
        );
      default:
        return '';
    }
  };

  const getFeedbackLabel = () => {
    if (feedbackSubmitted) {
      return 'Feedback submitted';
    }
    return userType === 'client' || isListingAgent
      ? 'No feedback yet'
      : 'Feedback incomplete';
  };

  const getFeedbackButton = () => {
    if (feedbackSubmitted) {
      return (
        <StyledFeedbackContainer>
          <button style={{ color: theme.blue6 }} onClick={scrollToFeedback}>
            View feedback
          </button>

          {/* ENG-14178 - allow coop agents to send feedback to clients */}
          {(isListingAgent || userType === 'coop') && (
            <>
              <StyledDot> • </StyledDot>
              <SendToClients
                appointment={appointment}
                appointmentConfig={listingAppointmentConfig}
              />
            </>
          )}
        </StyledFeedbackContainer>
      );
    }

    if (isListingAgent) {
      return (
        <button
          data-cy="send-feedback-reminder-btn"
          style={{ color: theme.blue6 }}
          onClick={sendFeedbackReminder}
        >
          Send Reminder
        </button>
      );
    }

    if (userType === 'client') {
      return null;
    }

    return (
      <button style={{ color: theme.blue6 }} onClick={scrollToFeedback}>
        Give Feedback
      </button>
    );
  };

  const sendFeedbackReminder = async () => {
    try {
      await FeedbackController.sendFeedbackReminder(listing.id, appointment.id);
      message.success('Email sent!');
    } catch (err) {
      message.error(err.response.data.message);
      errorReporter.error(err);
    }
  };

  let calendarAlmanacUrl: string | undefined;
  let registerOfferUrl = `/#/listing/${listing.id}/offer/new?view=listing`;
  if (dashboardId) {
    calendarAlmanacUrl =
      userType === 'rets'
        ? `/external/rets/${dashboardId}/almanac`
        : `/external/dash/${dashboardId}/almanac`;
    registerOfferUrl =
      userType === 'rets'
        ? `/dashboard/#/rets/${dashboardId}/appointments/offer`
        : `/external/#/dash/${dashboardId}/offers`;
  }

  return (
    <>
      <Section title="Details" data-cy="details-section">
        {[
          'new',
          'attempted',
          'time_change',
          'suggested_time_change',
          'confirm',
        ].includes(appointment.status) &&
          !appointment.passed &&
          userType !== 'client' && (
            <>
              <DetailRow icon="directions" data-cy="detail-get-directions">
                <button
                  style={{ color: theme.blue6 }}
                  onClick={clickDirections}
                >
                  Get directions
                </button>
              </DetailRow>

              <DetailRow
                icon="antdCalendarFilled"
                data-cy="detail-calendar-sync"
              >
                <CoopCalendarSyncButton
                  appointment={appointment}
                  almanacUrl={calendarAlmanacUrl}
                  {...(userType === 'rets'
                    ? { retsId: dashboardId }
                    : { dashboardId })}
                  newDetailsPageDesign
                  queryParams={queryParams}
                />
              </DetailRow>
            </>
          )}
        {hasFeature('OFFERS' as Feature) && offerDetails && (
          <DetailRow icon="pencilFilled" data-cy="detail-registered-offers">
            <>
              {typeof offerDetails.offerCount === 'number' && (
                <>
                  {offerDetails.offerCount} offer
                  {offerDetails.offerCount !== 1 ? 's' : ''} registered <br />
                </>
              )}

              <StyledRegisterOfferLink href={registerOfferUrl}>
                Register offer
              </StyledRegisterOfferLink>
            </>
          </DetailRow>
        )}
        {showFeedback && (
          <DetailRow icon="antdLikeFilled" data-cy="detail-feedback">
            <>
              {getFeedbackLabel()}
              <br />
              {getFeedbackButton()}
            </>
          </DetailRow>
        )}
        {(['signed', 'uploaded', 'overridden', 'uploadedLA'].includes(
          covid19State.status,
        ) ||
          ([
            'new',
            'attempted',
            'time_change',
            'suggested_time_change',
            'confirm',
          ].includes(appointment.status) &&
            appointment.covid19RequestedAck)) && (
          <DetailRow icon="fileText" data-cy="detail-covid-waiver">
            {getCovid19Text()}
          </DetailRow>
        )}
        {tourDetails && (
          <DetailRow icon="locationCompanyFilled" data-cy="detail-tour-info">
            <DashboardTour
              tour={tourDetails}
              simplifiedLayout
              fromTourPage={fromTourPage}
            />
          </DetailRow>
        )}
        <DetailRow icon="userHome" data-cy="detail-appointment-type">
          <>{correctShowingTypeLabel(appointment.type)}</>
        </DetailRow>
        {[
          'new',
          'attempted',
          'time_change',
          'suggested_time_change',
          'confirm',
        ].includes(appointment.status) &&
          userType === 'coop' &&
          !dashboardId && (
            <DetailRow
              icon="antdUserGroupAddOutlined"
              data-cy="detail-invite-buyers"
            >
              <>
                <strong>Invite buyers</strong>
                <StyledInviteBuyerContainer>
                  <StyledSearchBar>
                    <InviteBuyer
                      agent={appointment.coop}
                      value={inviteBuyers}
                      listing={listing}
                      onValueChange={(buyers: any) => {
                        setInviteBuyers(buyers);
                        setBuyersUpdated(true);
                      }}
                    />
                  </StyledSearchBar>
                  <Button
                    onClick={updateBuyers}
                    loading={savingBuyers}
                    disabled={!buyersUpdated}
                  >
                    Update
                  </Button>
                </StyledInviteBuyerContainer>
              </>
            </DetailRow>
          )}
      </Section>
      <MobileMapsDrawer
        visible={iosDrawerVisible}
        setVisible={setIosDrawerVisible as any}
        destinationAddress={destinationAddress}
      />
    </>
  );
};

export default DetailsSection;

const StyledDot = styled.span`
  color: ${theme.gray7};
  margin: 0px 5px;
`;

const StyledFeedbackContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSearchBar = styled.div`
  min-width: 20vh;
`;

const StyledInviteBuyerContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledRegisterOfferLink = styled.a`
  color: ${theme.blue6};
`;
